import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;

export class DragZoomWrapper extends HTMLElement {
  #controller = new AbortController();
  /** @type {number} */
  #scale = MIN_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = MIN_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {Point} */
  #startMidpoint = { x: 0, y: 0 };
  /** @type {boolean} */
  #pointerMoved = false;
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #wasPinching = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {boolean} */
  #highResLoaded = false;

  get #image() {
    return this.querySelector('img');
  }

  connectedCallback() {
    if (!this.#image) return;

    this.#initResizeListener();
    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);

    this.addEventListener('pointerdown', this.#handlePointerDown, options);
    this.addEventListener('pointermove', this.#handlePointerMove, options);
    this.addEventListener('pointerup', this.#handlePointerUp, options);
    this.addEventListener('pointercancel', this.#handlePointerUp, options);
    this.addEventListener('click', this.#handleClick, options);
    this.addEventListener('wheel', this.#handleWheel, options);

    this.#image?.addEventListener('load', this.#updateTransform, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (!this.#initialized && isMobileBreakpoint()) {
      this.#initEventListeners();
    }
    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    preventDefault(event);

    if (event.touches.length === 2) {
      const [point1, point2] = Array.from(event.touches).map(touchToPoint);
      if (!point1 || !point2) return;
      this.#startZoomGesture(point1, point2);
    } else if (event.touches.length === 1) {
      const point = touchToPoint(event.touches[0]);
      if (!point) return;

      this.#startDragGesture(point);
    }
  };

  /**
   * Start a zoom gesture with two points
   * @param {Point} point1
   * @param {Point} point2
   */
  #startZoomGesture(point1, point2) {
    const rect = this.getBoundingClientRect();
    const midpoint = getMidpoint(point1, point2);
    this.#initialDistance = getDistance(point1, point2);
    this.#startScale = this.#scale;
    this.#startMidpoint = { x: midpoint.x - rect.left, y: midpoint.y - rect.top };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = false;
    this.#wasPinching = true;
  }

  /**
   * Start a drag gesture with a single point
   * @param {Point} point
   */
  #startDragGesture(point) {
    this.#startPosition = { x: point.x, y: point.y };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#pointerMoved = false;
    this.#isDragging = true;
    this.#wasPinching = false;
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    preventDefault(event);

    const isZooming = event.touches.length === 2;
    const isDragging = event.touches.length === 1 && this.#isDragging;

    if (isZooming) {
      this.#processZoomGesture(event);
    } else if (isDragging) {
      this.#processDragGesture(event);
    }
  };

  /**
   * Process zoom gesture from touch event
   * @param {TouchEvent} event
   */
  #processZoomGesture(event) {
    const [point1, point2] = Array.from(event.touches).map(touchToPoint);
    if (!point1 || !point2) return;

    const rect = this.getBoundingClientRect();
    const currentMidpoint = getMidpoint(point1, point2);
    const currentDistance = getDistance(point1, point2);

    const newScale = clamp(
      (currentDistance / this.#initialDistance) * this.#startScale,
      MIN_ZOOM,
      MAX_ZOOM
    );
    this.#scale = newScale;

    const containerCenter = { x: this.clientWidth / 2, y: this.clientHeight / 2 };
    const localMidpoint = { x: currentMidpoint.x - rect.left, y: currentMidpoint.y - rect.top };

    // Compute translate absolutely from gesture start to avoid per-frame drift.
    // Keeps the image point under the initial midpoint anchored as scale and pan change.
    this.#translate = {
      x: (localMidpoint.x - containerCenter.x) / newScale - (this.#startMidpoint.x - containerCenter.x) / this.#startScale + this.#startTranslate.x,
      y: (localMidpoint.y - containerCenter.y) / newScale - (this.#startMidpoint.y - containerCenter.y) / this.#startScale + this.#startTranslate.y,
    };

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * Process drag gesture from touch event or pointer point
   * @param {TouchEvent | Point} event
   */
  #processDragGesture(event) {
    const point = 'touches' in event ? touchToPoint(event.touches[0]) : event;
    if (!point) return;

    const minZoom = this.#getMinZoom();
    if (!minZoom || this.#scale <= minZoom + 0.01) return;

    if (!this.#pointerMoved) {
      const dx = point.x - this.#startPosition.x;
      const dy = point.y - this.#startPosition.y;
      if (Math.hypot(dx, dy) > 8) this.#pointerMoved = true;
      else return;
    }

    // Calculate new translation
    this.#translate = {
      x: this.#startTranslate.x + (point.x - this.#startPosition.x) / this.#scale,
      y: this.#startTranslate.y + (point.y - this.#startPosition.y) / this.#scale,
    };

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      const wasTap = !this.#pointerMoved && !this.#wasPinching;
      this.#isDragging = false;
      this.#wasPinching = false;

      // preventDefault on touchstart suppresses the synthetic click event on mobile,
      // so tap-to-zoom must be handled here instead of #handleClick.
      if (wasTap) {
        const minZoom = this.#getMinZoom();
        if (minZoom) {
          if (this.#scale > minZoom + 0.01) {
            this.#scale = minZoom;
            this.#translate = { x: 0, y: 0 };
          } else {
            this.#scale = this.#getCoverZoom() ?? clamp(minZoom * 2, minZoom, MAX_ZOOM);
            this.#translate = { x: 0, y: 0 };
          }
        }
      }

      this.#requestUpdateTransform();
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;
    preventDefault(event);

    this.setPointerCapture(event.pointerId);
    const point = pointerToPoint(event);
    if (!point) return;

    this.#startDragGesture(point);
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    if (!this.#isDragging) return;
    preventDefault(event);

    const point = pointerToPoint(event);
    if (!point) return;

    this.#processDragGesture(point);
  };

  #handlePointerUp = () => {
    this.#isDragging = false;
    this.#requestUpdateTransform();
  };

  /**
   * @param {MouseEvent | PointerEvent} event
   */
  #handleClick = (event) => {
    if (this.#pointerMoved || event.button !== 0) return;
    preventDefault(event);

    const minZoom = this.#getMinZoom();
    if (!minZoom) return;

    if (this.#scale > minZoom + 0.01) {
      this.#scale = minZoom;
      this.#translate = { x: 0, y: 0 };
    } else {
      this.#scale = this.#getCoverZoom() ?? clamp(minZoom * 2, minZoom, MAX_ZOOM);
      this.#translate = { x: 0, y: 0 };
    }

    this.#requestUpdateTransform();
  };

  /**
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    preventDefault(event);

    const oldScale = this.#scale;
    // Trackpad pinch fires wheel events with ctrlKey and small deltaY; use proportional
    // scaling so each tiny finger movement doesn't apply a full 8% jump.
    const scaleFactor = event.ctrlKey
      ? Math.pow(0.998, event.deltaY)
      : event.deltaY > 0 ? 0.92 : 1.08;
    const newScale = clamp(oldScale * scaleFactor, MIN_ZOOM, MAX_ZOOM);
    if (newScale === oldScale) return;

    const rect = this.getBoundingClientRect();
    const pointer = { x: event.clientX - rect.left, y: event.clientY - rect.top };
    const containerCenter = { x: this.clientWidth / 2, y: this.clientHeight / 2 };

    // Keep the point under the cursor stationary: adjust translate in image-space
    this.#translate.x += (pointer.x - containerCenter.x) * (1 / newScale - 1 / oldScale);
    this.#translate.y += (pointer.y - containerCenter.y) * (1 / newScale - 1 / oldScale);
    this.#scale = newScale;
    this.#requestUpdateTransform();
  };

  /**
   * Get the cover zoom: the scale at which the image content fills the wrapper completely.
   * The image renders at full wrapper size via object-fit: contain, so the contained content
   * dimensions drive this calculation (not the element box dimensions).
   * @returns {number | null}
   */
  #getCoverZoom = () => {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    const imageDimensions = this.#getImageDimensions();
    if (!imageDimensions || !containerWidth || !containerHeight) return null;
    const { width: imageWidth, height: imageHeight } = imageDimensions;
    return clamp(
      Math.max(containerWidth / imageWidth, containerHeight / imageHeight),
      MIN_ZOOM,
      MAX_ZOOM
    );
  };

  /**
   * Get the minimum zoom for the image.
   * With the image rendering at full wrapper size (object-fit: contain), the initial
   * contain view is achieved at scale=1, so MIN_ZOOM is always the minimum.
   * @returns {number | null}
   */
  #getMinZoom = () => {
    if (!this.#image) return null;
    return MIN_ZOOM;
  };

  /**
   * Get the dimensions of the image
   * @returns {{ width: number, height: number } | null}
   */
  #getImageDimensions = () => {
    const containerRect = this.getBoundingClientRect();
    if (!this.#image) return null;
    const { naturalWidth, naturalHeight } = this.#image;
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;

    if (!naturalWidth || !naturalHeight || !containerWidth || !containerHeight) return null;

    const containerAspect = containerRect.width / containerRect.height;
    const naturalAspect = naturalWidth / naturalHeight;

    let imageWidth, imageHeight;
    if (naturalAspect > containerAspect) {
      // Image is wider than container (relative to height)
      imageWidth = containerRect.width;
      imageHeight = imageWidth / naturalAspect;
    } else {
      // Image is taller than container (relative to width)
      imageHeight = containerRect.height;
      imageWidth = imageHeight * naturalAspect;
    }

    if (imageWidth === 0 || imageHeight === 0) return null;

    return { width: imageWidth, height: imageHeight };
  };

  /**
   * Constrain image translation to keep it within the viewport
   */
  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight) return;

    const minZoom = this.#getMinZoom();
    if (!minZoom) return;

    this.#scale = clamp(this.#scale, minZoom, MAX_ZOOM);

    const imageDimensions = this.#getImageDimensions();
    if (!imageDimensions) return;

    const { width: imageWidth, height: imageHeight } = imageDimensions;

    const scaledWidth = imageWidth * this.#scale;
    const scaledHeight = imageHeight * this.#scale;

    // Calculate how much the image extends beyond container
    const excessWidth = Math.max(0, scaledWidth - containerWidth);
    const excessHeight = Math.max(0, scaledHeight - containerHeight);

    const maxTranslateX = excessWidth / (2 * this.#scale);
    const maxTranslateY = excessHeight / (2 * this.#scale);

    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);
  }

  /**
   * Request an animation frame to update the transform
   */
  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  /**
   * Cancel any pending animation frame
   */
  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #maybeUpgradeToHighRes() {
    if (this.#highResLoaded || !this.#image) return;
    const minZoom = this.#getMinZoom() ?? MIN_ZOOM;
    if (this.#scale <= minZoom + 0.05) return;
    const highResUrl = this.#image.dataset.maxResolution;
    if (!highResUrl) return;
    this.#highResLoaded = true;
    const loader = new Image();
    loader.onload = () => {
      if (!this.#image) return;
      this.#image.removeAttribute('srcset');
      this.#image.removeAttribute('sizes');
      this.#image.src = highResUrl;
    };
    loader.src = highResUrl;
  }

  #updateTransform = () => {
    this.#animationFrame = null;
    this.#constrainTranslation();
    this.#maybeUpgradeToHighRes();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  };

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * Convert a Touch object to a Point object
 * @param {Touch | undefined} touch
 * @returns {Point | undefined}
 */
function touchToPoint(touch) {
  if (!touch) return undefined;
  return { x: touch.clientX, y: touch.clientY };
}

/**
 * Convert a PointerEvent object to a Point
 * @param {PointerEvent} event
 * @returns {Point | undefined}
 */
function pointerToPoint(event) {
  if (!event) return undefined;
  return { x: event.clientX, y: event.clientY };
}

/**
 * Calculate the distance between two points
 * @param {Point} point1 - First point
 * @param {Point} point2 - Second point
 * @returns {number} The distance between the points
 */
function getDistance(point1, point2) {
  const dx = point1.x - point2.x;
  const dy = point1.y - point2.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calculate the midpoint between two points
 * @param {Point} point1 - First point
 * @param {Point} point2 - Second point
 * @returns {Point} The midpoint
 */
function getMidpoint(point1, point2) {
  return {
    x: (point1.x + point2.x) / 2,
    y: (point1.y + point2.y) / 2,
  };
}
